import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { Form, Checkbox, Radio, Input, Button, Tag, notification, Spin } from 'antd'

import {
  fetchAppraiserSpecializations,
  saveAppraiserSpecializations,
} from '@actions/appraiserSpecializations'
import {
  selectAppraiserSpecializationsData,
  selectAppraiserSpecializationsStatus,
} from '@reducers/appraiserSpecializations/appraiserSpecializationsSelectors'
import { LOADING } from '@constants/fetchStatuses'

import './MySpecializations.scss'

const PRODUCT_PDC = 'product_pdc'

const PRODUCT_OPTIONS = [
  { value: 'product_traditional', label: 'Traditional Full Appraisal' },
  { value: 'product_hybrid_appraisals', label: 'Hybrid Appraisals' },
  { value: 'product_desktop', label: 'Desktop' },
  { value: 'product_virtual_inspection', label: 'Virtual Inspection' },
  { value: PRODUCT_PDC, label: 'PDC (Property Data Collection)' },
]
const PRODUCT_TAG_VALUES = PRODUCT_OPTIONS.map((o) => o.value)

const VALUATION_OPTIONS = [
  { value: 'valuation_pdc_yes', label: 'Yes' },
  { value: 'valuation_pdc_no', label: 'No' },
]
const VALUATION_TAG_VALUES = VALUATION_OPTIONS.map((o) => o.value)

const LOAN_OPT_OUT = 'loan_opt_out'
const LOAN_OPTIONS = [
  { value: 'loan_fha', label: 'FHA' },
  { value: 'loan_va', label: 'VA' },
  { value: 'loan_usda', label: 'USDA' },
]
const LOAN_OPT_OUT_LABEL = 'I only do conventional'
const LOAN_TAG_VALUES = [...LOAN_OPTIONS.map((o) => o.value), LOAN_OPT_OUT]

const PROPERTY_OPT_OUT = 'property_opt_out'
const PROPERTY_OPTIONS = [
  { value: 'property_2_4_unit', label: '2–4 Unit' },
  { value: 'property_coop', label: 'Co-op' },
  { value: 'property_green_building', label: 'Green Building' },
  { value: 'property_land', label: 'Land/Lot' },
  { value: 'property_manufactured', label: 'Manufactured' },
  { value: 'property_mixed_use', label: 'Mixed-use' },
  { value: 'property_multi_5_plus', label: 'Multi-family (5+)' },
  { value: 'property_new_construction', label: 'New Construction' },
]
const PROPERTY_OPT_OUT_LABEL = 'I only handle standard residential'
const PROPERTY_TAG_VALUES = [...PROPERTY_OPTIONS.map((o) => o.value), PROPERTY_OPT_OUT]

const VAL_AS_IS_AS_REPAIRED_TAG = 'valuation_as_is_as_repaired'
const VAL_AS_IS_AS_REPAIRED_TAG_VALUES = [VAL_AS_IS_AS_REPAIRED_TAG]
const VAL_AS_IS_AS_REPAIRED_LABEL = 'As-Is and/or As-Repaired (subject-to) values'

const FORM_SOFTWARE_OTHER = 'other'
const FORM_SOFTWARE_NONE = 'none'
const FORM_SOFTWARE_OPTIONS = [
  { value: 'aci', label: 'ACI' },
  { value: 'bradford', label: 'Bradford' },
  { value: 'clickforms', label: 'ClickFORMS' },
  { value: 'sfrep', label: 'SFREP / Mercury' },
  { value: 'total', label: 'TOTAL by a la mode' },
  { value: 'reggora', label: 'Reggora Form Software' },
  { value: FORM_SOFTWARE_OTHER, label: 'Other' },
]

const UAD_36_OPTIONS = [
  { value: 'completed', label: "I've completed UAD 3.6 appraisals" },
  { value: 'ready', label: "Not yet, but I'm ready to take them" },
  { value: 'non_uad_only', label: 'I only accept non-UAD appraisals' },
]

const YEARS_OPTIONS = [
  { value: 'lt_1', label: '< 1' },
  { value: 'y_1_3', label: '1–3' },
  { value: 'y_4_7', label: '4–7' },
  { value: 'y_8_15', label: '8–15' },
  { value: 'y_16_plus', label: '16+' },
]

const EMPTY_FORM = {
  selectedTags: [],
  formSoftwareUsed: [],
  formSoftwareUsedOtherText: '',
  uad36Readiness: null,
  yearsOfExperience: null,
}

const buildFormStateFromServer = (data) => {
  if (!data) { return EMPTY_FORM }
  return {
    selectedTags: data.selected_tags ? [...data.selected_tags] : [],
    formSoftwareUsed: data.form_software_used ? [...data.form_software_used] : [],
    formSoftwareUsedOtherText: data.form_software_used_other_text || '',
    uad36Readiness: data.uad_36_readiness || null,
    yearsOfExperience: data.years_of_experience || null,
  }
}

const buildPayload = (form) => {
  const productPdcSelected = form.selectedTags.includes(PRODUCT_PDC)
  const cleanedTags = productPdcSelected
    ? form.selectedTags
    : form.selectedTags.filter((t) => !VALUATION_TAG_VALUES.includes(t))
  const includesOther = form.formSoftwareUsed.includes(FORM_SOFTWARE_OTHER)
  return {
    selected_tags: cleanedTags,
    form_software_used: form.formSoftwareUsed,
    form_software_used_other_text: includesOther
      ? form.formSoftwareUsedOtherText.trim()
      : null,
    uad_36_readiness: form.uad36Readiness,
    years_of_experience: form.yearsOfExperience,
  }
}

const MySpecializations = ({
  onSaveSuccess = undefined,
  saveButtonLabel = 'Save',
  hideBadge = false,
}) => {
  const dispatch = useDispatch()
  const serverPayload = useSelector(selectAppraiserSpecializationsData)
  const status = useSelector(selectAppraiserSpecializationsStatus)

  const [form, setForm] = useState(EMPTY_FORM)
  const [initialSnapshot, setInitialSnapshot] = useState(EMPTY_FORM)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    dispatch(fetchAppraiserSpecializations())
  }, [dispatch])

  useEffect(() => {
    if (!serverPayload) { return }
    const snapshot = buildFormStateFromServer(serverPayload)
    setForm(snapshot)
    setInitialSnapshot(snapshot)
  }, [serverPayload])

  const isDirty = useMemo(() => !isEqual(form, initialSnapshot), [form, initialSnapshot])

  const toggleTagFromGroup = (tag, groupValues, optOutValue) => {
    setForm((prev) => {
      const tagsInGroup = new Set(groupValues)
      const otherTags = prev.selectedTags.filter((t) => !tagsInGroup.has(t))
      const currentInGroup = new Set(prev.selectedTags.filter((t) => tagsInGroup.has(t)))
      let newGroup
      if (currentInGroup.has(tag)) {
        currentInGroup.delete(tag)
        newGroup = currentInGroup
      } else {
        if (optOutValue && tag === optOutValue) {
          newGroup = new Set([optOutValue])
        } else if (optOutValue && currentInGroup.has(optOutValue)) {
          newGroup = new Set([tag])
        } else {
          currentInGroup.add(tag)
          newGroup = currentInGroup
        }
      }
      return { ...prev, selectedTags: [...otherTags, ...newGroup] }
    })
  }

  const setSingleTag = (tag, groupValues) => {
    setForm((prev) => {
      const tagsInGroup = new Set(groupValues)
      const otherTags = prev.selectedTags.filter((t) => !tagsInGroup.has(t))
      return { ...prev, selectedTags: [...otherTags, tag] }
    })
  }

  const toggleFormSoftware = (value) => {
    setForm((prev) => {
      const current = new Set(prev.formSoftwareUsed)
      const alreadyChecked = current.has(value)

      if (alreadyChecked) {
        current.delete(value)
        const next = Array.from(current)
        return {
          ...prev,
          formSoftwareUsed: next,
          formSoftwareUsedOtherText: next.includes(FORM_SOFTWARE_OTHER)
            ? prev.formSoftwareUsedOtherText
            : '',
        }
      }

      if (value === FORM_SOFTWARE_NONE) {
        return {
          ...prev,
          formSoftwareUsed: [FORM_SOFTWARE_NONE],
          formSoftwareUsedOtherText: '',
        }
      }

      current.delete(FORM_SOFTWARE_NONE)
      current.add(value)
      return {
        ...prev,
        formSoftwareUsed: Array.from(current),
      }
    })
  }

  const onSave = async () => {
    setIsSaving(true)
    try {
      const payload = buildPayload(form)
      const savedPayload = await dispatch(saveAppraiserSpecializations(payload))
      notification.success({ message: 'Specializations saved' })
      if (onSaveSuccess) {
        onSaveSuccess(savedPayload)
      }
    } catch (err) {
      const message = (err && err.error) || 'Failed to save specializations'
      notification.error({ message, description: 'Something went wrong.' })
    } finally {
      setIsSaving(false)
    }
  }

  const allSetVisible = Boolean(
    !hideBadge && serverPayload && serverPayload.is_specializations_complete,
  )

  const productPdcSelected = form.selectedTags.includes(PRODUCT_PDC)
  const showOtherInput = form.formSoftwareUsed.includes(FORM_SOFTWARE_OTHER)
  const noneSelected = form.formSoftwareUsed.includes(FORM_SOFTWARE_NONE)
  const isLoadingInitial = status === LOADING && !serverPayload

  const isInGroup = (tag) => form.selectedTags.includes(tag)
  const isSoftwareChecked = (value) => form.formSoftwareUsed.includes(value)

  return (
    <div className="my-specializations">
      {allSetVisible && (
        <Tag color="success" className="my-specializations__badge" data-testid="all-set-badge">
          All set ✓
        </Tag>
      )}
      <p className="my-specializations__subtitle" data-testid="subtitle">
        Tell us what you do so we can match you with the right orders. Takes about 2 minutes.
      </p>

      {isLoadingInitial
        ? (
          <div className="my-specializations__loading"><Spin /></div>
          )
        : (
          <Form layout="vertical" onSubmitCapture={(e) => e.preventDefault()} className="my-specializations__form">
            <section className="my-specializations__section" data-testid="section-what-you-do">
              <div className="my-specializations__section-heading">
                <h3 className="my-specializations__section-title">What you do</h3>
                <span
                  className="my-specializations__section-description"
                  data-testid="section-description-what-you-do"
                >
                  The products, programs, and property types you take on.
                </span>
              </div>

              <Form.Item label="Products">
                <Checkbox.Group
                  value={form.selectedTags.filter((t) => PRODUCT_TAG_VALUES.includes(t))}
                  className="my-specializations__checkbox-grid"
                >
                  {PRODUCT_OPTIONS.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      value={opt.value}
                      checked={isInGroup(opt.value)}
                      onChange={() => toggleTagFromGroup(opt.value, PRODUCT_TAG_VALUES, null)}
                    >
                      {opt.label}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>

              {productPdcSelected && (
                <div data-testid="pdc-value-opinions-group">
                  <Form.Item label="Do you complete value opinions on PDC work?">
                    <Radio.Group
                      value={form.selectedTags.find((t) => VALUATION_TAG_VALUES.includes(t)) || null}
                      onChange={(e) => setSingleTag(e.target.value, VALUATION_TAG_VALUES)}
                    >
                      {VALUATION_OPTIONS.map((opt) => (
                        <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </div>
              )}

              <Form.Item label="Loan Programs">
                <Checkbox.Group
                  className="my-specializations__checkbox-grid"
                  value={form.selectedTags.filter((t) => LOAN_TAG_VALUES.includes(t))}
                >
                  <Checkbox
                    value={LOAN_OPT_OUT}
                    checked={isInGroup(LOAN_OPT_OUT)}
                    onChange={() => toggleTagFromGroup(LOAN_OPT_OUT, LOAN_TAG_VALUES, LOAN_OPT_OUT)}
                  >
                    {LOAN_OPT_OUT_LABEL}
                  </Checkbox>
                  {LOAN_OPTIONS.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      value={opt.value}
                      checked={isInGroup(opt.value)}
                      onChange={() => toggleTagFromGroup(opt.value, LOAN_TAG_VALUES, LOAN_OPT_OUT)}
                    >
                      {opt.label}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                label={(
                  <span>
                    Property Types{' '}
                    <span className="my-specializations__helper-text">
                      Beyond standard residential, which do you also handle?
                    </span>
                  </span>
                )}
              >
                <Checkbox.Group
                  className="my-specializations__checkbox-grid"
                  value={form.selectedTags.filter((t) => PROPERTY_TAG_VALUES.includes(t))}
                >
                  <Checkbox
                    value={PROPERTY_OPT_OUT}
                    checked={isInGroup(PROPERTY_OPT_OUT)}
                    onChange={() => toggleTagFromGroup(PROPERTY_OPT_OUT, PROPERTY_TAG_VALUES, PROPERTY_OPT_OUT)}
                  >
                    {PROPERTY_OPT_OUT_LABEL}
                  </Checkbox>
                  {PROPERTY_OPTIONS.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      value={opt.value}
                      checked={isInGroup(opt.value)}
                      onChange={() => toggleTagFromGroup(opt.value, PROPERTY_TAG_VALUES, PROPERTY_OPT_OUT)}
                    >
                      {opt.label}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>

              <div data-testid="valuation-scenarios-group">
                <Form.Item label="Valuation scenarios">
                  <Checkbox.Group
                    className="my-specializations__checkbox-grid"
                    value={form.selectedTags.filter((t) => VAL_AS_IS_AS_REPAIRED_TAG_VALUES.includes(t))}
                  >
                    <Checkbox
                      value={VAL_AS_IS_AS_REPAIRED_TAG}
                      checked={isInGroup(VAL_AS_IS_AS_REPAIRED_TAG)}
                      onChange={() => toggleTagFromGroup(
                        VAL_AS_IS_AS_REPAIRED_TAG,
                        VAL_AS_IS_AS_REPAIRED_TAG_VALUES,
                        null,
                      )}
                    >
                      {VAL_AS_IS_AS_REPAIRED_LABEL}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              </div>
            </section>

            <section className="my-specializations__section" data-testid="section-how-you-work">
              <div className="my-specializations__section-heading">
                <h3 className="my-specializations__section-title">How you work</h3>
                <span
                  className="my-specializations__section-description"
                  data-testid="section-description-how-you-work"
                >
                  Your tools, UAD 3.6 experience, and time in the field.
                </span>
              </div>

              <Form.Item
                label={(
                  <span>
                    Which form software do you use?{' '}
                    <span className="my-specializations__helper-text">(select all that apply)</span>
                  </span>
                )}
              >
                <Checkbox.Group
                  className="my-specializations__checkbox-grid"
                  value={form.formSoftwareUsed}
                >
                  {FORM_SOFTWARE_OPTIONS.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      value={opt.value}
                      checked={isSoftwareChecked(opt.value)}
                      onChange={() => toggleFormSoftware(opt.value)}
                    >
                      {opt.label}
                    </Checkbox>
                  ))}
                  <Checkbox
                    value={FORM_SOFTWARE_NONE}
                    checked={noneSelected}
                    onChange={() => toggleFormSoftware(FORM_SOFTWARE_NONE)}
                    data-testid="form-software-none"
                  >
                    None
                  </Checkbox>
                </Checkbox.Group>
                {showOtherInput && (
                  <Input
                    className="my-specializations__other-input"
                    placeholder="Please specify…"
                    value={form.formSoftwareUsedOtherText}
                    onChange={(e) => setForm((prev) => ({ ...prev, formSoftwareUsedOtherText: e.target.value }))}
                    maxLength={200}
                    data-testid="other-software-input"
                  />
                )}
              </Form.Item>

              <Form.Item
                label={(
                  <span>
                    UAD 3.6{' '}
                    <span className="my-specializations__helper-text">
                      Where are you with the new appraisal standard?
                    </span>
                  </span>
                )}
              >
                <Radio.Group
                  value={form.uad36Readiness || null}
                  onChange={(e) => setForm((prev) => ({ ...prev, uad36Readiness: e.target.value }))}
                >
                  {UAD_36_OPTIONS.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>

              <Form.Item label="Years of appraisal experience">
                <Radio.Group
                  value={form.yearsOfExperience || null}
                  onChange={(e) => setForm((prev) => ({ ...prev, yearsOfExperience: e.target.value }))}
                >
                  {YEARS_OPTIONS.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </section>

            <div className="my-specializations__actions">
              <Button
                type="primary"
                onClick={onSave}
                disabled={!isDirty || isSaving}
                loading={isSaving}
                data-testid="save-specializations-button"
              >
                {saveButtonLabel}
              </Button>
              {isDirty && !isSaving && (
                <span className="my-specializations__dirty-affordance" data-testid="dirty-affordance">
                  You have unsaved changes
                </span>
              )}
            </div>
          </Form>
          )}
    </div>
  )
}

export default MySpecializations
